import { z } from 'zod'
import { bookingFilterStatuses } from '@/entities/booking'
import { internalPath } from '@/shared/lib/internal-path'

/**
 * Открытая карточка брони — глобальный search-параметр корневого роута.
 * Модалку открывают с пяти экранов; отдельный роут размонтировал бы экран под ней. Сама она
 * смонтирована в `AppShell`, здесь только валидируемый параметр.
 *
 * ⚠️ ГЛАВНАЯ ЛОВУШКА ФАЙЛА: `booking` НЕ переживает навигацию, если `search` передан ОБЪЕКТОМ.
 * TanStack Router search не сливает — `navigate({ to, search: {...} })` заменяет строку запроса
 * целиком, дальше каждый роут домешивает только свои `.default()`, а у `booking` дефолта нет.
 * Проверено на живом роутере: `navigate({to:'/tasks'})` со страницы `/chess?booking=bk_1` отдаёт
 * `{kind:'all'}`.
 *
 * ⚠️ Чинить это глобальным `retainSearchParams(['booking'])` НЕЛЬЗЯ: открытая карточка поедет за
 * пользователем на другой экран и повиснет поверх задач.
 *
 * Правило: смена состояния ВНУТРИ экрана (фильтр, сортировка, закрытие модалки) — только функцией
 * `search: (prev) => ({ ...prev, … })`; переход НА ДРУГОЙ экран — объектом или без `search` вовсе,
 * и `booking` при этом отваливается, что и требуется. Голый объект в первом случае закроет карточку
 * при смене фильтра, а выглядеть это будет как «модалка сама схлопывается».
 */
export const rootSearchSchema = z.object({
  booking: z.string().optional(),
})

export const bookingsSearchSchema = z.object({
  /*
   * ⚠️ Значения берутся из ОДНОЙ копии словаря (`entities/booking`), а не переписаны здесь:
   * этой же копией чипы сверяют ключ, пришедший с сервера. Разъедься схема адреса с проверкой
   * чипов — чип рисовался бы, а нажатие на него роняло бы разбор адреса.
   *
   * Коды — доменные (`checked_in`, а не выдуманный нами `in`): `?status=` уходит на сервер как
   * есть, и переводить его по дороге было бы нечем, кроме словаря, который никто не проверяет.
   */
  status: z.enum(bookingFilterStatuses).default('all'),
  sort: z.enum(['new', 'old', 'amount']).default('new'),
})

export const chessSearchSchema = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
})

/**
 * Виды задач — ровно те, что знает контракт (`getTaskQueue`, параметр `kind`). Без `docs`:
 * шаг «Документы» из движка сценариев убран, а задача рождается из шага — значит задач
 * такого вида не бывает. Оставь его здесь — URL принимал бы `?kind=docs`, которого сервер
 * не знает, а чип фильтра вечно показывал бы ноль.
 */
export const tasksSearchSchema = z.object({
  // Отбора «мои / без исполнителя» здесь нет намеренно: исполнителей в системе не существует, и
  // очередь у компании одна (см. `getTaskQueue`). Оставь параметр — адрес принимал бы `?scope=mine`,
  // на который сервер отвечал бы всей очередью, то есть ссылка врала бы получателю.
  kind: z.enum(['all', 'pay', 'checkin', 'decision', 'deposit']).default('all'),
})

export const inboxSearchSchema = z.object({
  // ⚠️ `mine` сюда не заводить: исполнителя у переписки нет как понятия, отбирать «мои» не по чему.
  // `.catch('all')` гасит старые закладки `?filter=mine` в «Все» вместо падения роутера.
  filter: z.enum(['all', 'ai']).catch('all'),
  conversation: z.string().optional(),
})

export const studioSearchSchema = z.object({
  scenario: z.string().optional(),
  content: z.string().optional(),
  /**
   * Вкладка живёт в адресе, а не в состоянии: «покажи, у каких квартир нет своего текста» — это то,
   * чем делятся ссылкой.
   *
   * ⚠️ НЕОБЯЗАТЕЛЬНЫЙ, и это не небрежность. Обе формы (`.default()` и `.catch(v)`) делают поле
   * обязательным на ВЫХОДЕ схемы, но роутер типизирует объектную форму `search` по ВХОДУ
   * (`ResolveSearchSchemaInput`), а там обязательным становится только `.catch(v)`. То есть
   * `.catch('list')` заставил бы КАЖДЫЙ переход объектом по Студии подставлять вкладку — и это
   * ровно ловушка из шапки файла. `.default()` вход не ломает, но и не нужен: отсутствие вкладки и
   * есть список. `catch(undefined)` гасит чужое значение вместо падения роутера.
   */
  tab: z.enum(['list', 'apartments']).optional().catch(undefined),
  /**
   * Разрез по квартирам, суженный до ОДНОГО текста (его роль). Пустой — разрез по всем.
   *
   * Живёт в адресе по той же причине, что и вкладка. ⚠️ Роль, а не идентификатор записи: сужение про
   * ТЕКСТ, а у текста с переопределениями записей несколько, и любая назвала бы столбец частным
   * случаем.
   *
   * Имеет смысл только вместе с `tab=apartments`; на списке не влияет ни на что — гасить его при
   * уходе со вкладки значило бы терять сужение при переключении туда-обратно.
   */
  scope: z
    .string()
    .optional()
    // Пустая строка — «параметра нет», а не «сужение до безымянного текста»: иначе `?scope=` открывал
    // бы экран «этого текста больше нет» на параметре, которого никто не задавал (так же у `import`).
    .transform((value) => value?.trim() || undefined),
  apartment: z.string().optional(),
  // Выбранный гость. Параметр вернулся вместе с разделом «Гости»: пока раздел был заглушкой, ссылка
  // из карточки брони вела бы в «Пока в планах», и адрес обещал бы конкретного человека, за которым
  // ничего нет. Теперь за ним есть экран, и «Открыть» из брони ведёт именно в него.
  client: z.string().optional(),
  /**
   * Открытая загрузка базы гостей. ⚠️ ЖИВЁТ В АДРЕСЕ ВЫНУЖДЕННО: применение идёт минутами, менеджера
   * при этом не запирают, а ручек `/client-imports*` ровно пять и списка среди них нет. Значит после
   * F5 или закрытия вкладки вернуться к идущему применению можно ТОЛЬКО по этому идентификатору.
   *
   * `new` — служебное значение для экрана выбора файла; отдельным флагом нельзя, два параметра про
   * одно состояние разъезжаются. Пустая строка отбрасывается: `?import=` — это «параметра нет», а
   * иначе экран открывал бы отчёт по пустому идентификатору.
   */
  import: z
    .string()
    .optional()
    .transform((value) => value?.trim() || undefined),
  // Куда подвести на разделе после возврата: редактор сценария отправляет сюда «Открыть маршрут»,
  // и без этого параметра кнопка просто закрывала бы редактор — то есть делала бы не то, что обещает.
  reveal: z.enum(['routing']).optional(),
})

/**
 * Возврат на саму страницу входа. Пропустить его — получить петлю уже ПОСЛЕ успешной
 * авторизации: вошёл → вернулся на `/login` → страница видит живую сессию → отправляет на
 * `redirect`, то есть снова на `/login` → и так вечно.
 */
const BACK_TO_LOGIN = /^\/login(?:[/?#]|$)/

/**
 * `redirect` — куда вернуть пользователя после входа. Кладёт его `App.tsx` при перехвате
 * 401 (см. `setupHttp`), но в адресной строке его может подменить кто угодно.
 *
 * ⚠️ ЭТО ЗАЩИТА ОТ ОТКРЫТОГО РЕДИРЕКТА, А НЕ ПРИДИРКА К ТИПАМ. Значение уезжает в
 * `/api/auth/login?redirect_uri=…`, и gateway после УСПЕШНОЙ авторизации отправляет браузер по
 * этому адресу. Без фильтра ссылка `…/login?redirect=https://evil.com` авторизует жертву у нас и
 * высаживает на чужой странице — с полным доверием к процессу, который она только что прошла.
 *
 * ⚠️ ФИЛЬТР СТОИТ В СХЕМЕ, А НЕ У КНОПКИ ВХОДА: схема — единственное место, через которое значение
 * попадает в приложение, у кнопки его можно забыть. Чужой адрес не роняет страницу, а отбрасывается.
 *
 * Сама проверка «это внутренний путь?» живёт в `shared/lib/internal-path`: у неё второй потребитель,
 * `Notification.href` (тоже строка с сервера, тоже уезжает в `navigate`), и две копии разъехались бы.
 */
export const loginSearchSchema = z.object({
  redirect: z
    .string()
    .optional()
    .transform((value) => {
      const path = internalPath(value)
      return path && !BACK_TO_LOGIN.test(path) ? path : undefined
    }),
})
